import classNames from 'classnames';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { Dimmer } from '~components/Dimmer/Dimmer';
import { PopupProps } from '~components/Popup/Popup.types';
import IconClose from '~public/static/images/icons/Close.svg';

import styles from './Popup.module.scss';

const POPUP_BOTTOM_MARGIN = 40;

export function Popup({
  children,
  active,
  onClose,
  isVisibleButtonClose = true,
  isOverlayClose = true,
  popupMod = 'center',
  scrollLock = true,
  ...rest
}: PopupProps) {
  const [isVisible, setIsVisible] = useState(
    popupMod !== 'bottom' || scrollLock,
  );
  const innerRef = useRef<HTMLDivElement>(null);

  const handleScroll = useCallback(() => {
    const hero = document.querySelector('[data-id="hero"]');
    const footer = document.querySelector('[data-id="footer"]');
    const innerElement = innerRef.current;

    if (!hero || !footer || !innerElement) return;

    const heroBottom = hero.getBoundingClientRect().bottom;
    const footerTop = footer.getBoundingClientRect().top;
    const windowHeight = window.innerHeight;
    const popupHeight = innerElement.offsetHeight;

    const isPastHero = heroBottom <= 0;

    const hideThreshold = popupHeight + POPUP_BOTTOM_MARGIN;
    const isBeforeFooter = footerTop > windowHeight - hideThreshold;

    setIsVisible(isPastHero && isBeforeFooter);
  }, []);

  function handleClose() {
    document.body.style.overflow = '';
    onClose();
  }

  function handleOverlayClick() {
    if (!isOverlayClose) return;
    handleClose();
  }

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onClose],
  );

  useEffect(() => {
    if (!active) return;
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [active, handleKeyDown]);

  useEffect(() => {
    if (!scrollLock) return;
    document.body.style.overflow = active ? 'hidden' : '';
  }, [active, scrollLock]);

  useEffect(() => {
    if (!active || popupMod !== 'bottom' || scrollLock) return;

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, [active, popupMod, handleScroll]);

  if (!active) {
    return null;
  }

  const closeButton = (
    <button className={styles.close} onClick={handleClose}>
      <IconClose />
    </button>
  );

  return createPortal(
    <Dimmer
      {...rest}
      onClick={handleOverlayClick}
      className={classNames(
        popupMod === 'bottom' && styles.dimmerBottom,
        isVisible && styles.dimmerVisible,
      )}
    >
      <div
        className={classNames(
          styles.popup,
          popupMod === 'bottom' && styles.popupBottom,
          isVisible && styles.popupVisible,
        )}
      >
        <div
          className={classNames(
            styles.inner,
            popupMod === 'bottom' && styles.innerBottom,
          )}
          onClick={(e) => e.stopPropagation()}
          ref={innerRef}
        >
          {isVisibleButtonClose && closeButton}
          {children}
        </div>
      </div>
    </Dimmer>,
    document.body,
  );
}
